import {
  type GrantRow,
  isSupplierAvailableFromGrants,
} from "../../../../shared/rbac/crossResourceAvailability";
import { buildUserGroupMemberships } from "../../../../shared/rbac/grantQueries";
import { roleLabel } from "../../../../shared/rbac/roles";
import { userDisplayName } from "../../../lib/userDisplayName";

export type NodeKind = "user" | "group" | "app" | "skill" | "space";

// `membership` (user → group) and `direct` (principal → scope grant) are the
// backbone the graph always draws. `inherited` (a user reaching a scope through
// a group) and `derived` (a space able to use a connector because of its
// members) are the effective-access edges that an API consumer can't reproduce.
export type EdgeType = "membership" | "direct" | "inherited" | "derived";

export type GraphNode = {
  id: string;
  kind: NodeKind;
  name: string;
  subtitle?: string;
  isAdmin?: boolean;
  spaceMode?: string;
};

export type GraphEdge = {
  id: string;
  sourceId: string;
  targetId: string;
  type: EdgeType;
  roleKey: string;
  roleLabel: string;
  viaGroupId?: string;
  viaGroupName?: string;
};

export type AccessModel = {
  nodes: GraphNode[];
  edges: GraphEdge[];
  nodeById: Map<string, GraphNode>;
};

// Broad enough for both the cross-resource availability check (GrantRow) and to
// resolve the role for an edge.
export type ModelGrant = GrantRow & {
  serviceAccountId?: string | null;
  customRoleId?: string | null;
};

export type GraphUserInput = {
  id: string;
  name: string | null;
  email: string;
  deactivated?: boolean | null;
};

export type GraphGroupInput = {
  id: string;
  name: string;
  roleGrantsAsScope: ReadonlyArray<{ userId: string | null }>;
};

export type GraphScopeInput = {
  id: string;
  name: string;
  roleGrantsAsScope: ReadonlyArray<ModelGrant>;
};

export type GraphSpaceInput = GraphScopeInput & { mode: string };

export type BuildAccessModelInput = {
  users: ReadonlyArray<GraphUserInput>;
  groups: ReadonlyArray<GraphGroupInput>;
  apps: ReadonlyArray<GraphScopeInput>;
  skills: ReadonlyArray<GraphScopeInput>;
  spaces: ReadonlyArray<GraphSpaceInput>;
  adminUserIds: ReadonlySet<string>;
  customRoles: ReadonlyArray<{ id: string; name: string }>;
};

const roleKeyOf = (g: ModelGrant) => g.builtinRoleId ?? g.customRoleId ?? "";

export function buildAccessModel(input: BuildAccessModelInput): AccessModel {
  const { users, groups, apps, skills, spaces, adminUserIds, customRoles } =
    input;
  const sharedSpaces = spaces.filter((s) => s.mode !== "personal");

  const userGroupMemberships = buildUserGroupMemberships(
    groups.flatMap((g) =>
      g.roleGrantsAsScope.map((r) => ({ userId: r.userId, scopeId: g.id }))
    )
  );

  const groupMembers = new Map<string, string[]>(
    groups.map((g) => [
      g.id,
      [
        ...new Set(
          g.roleGrantsAsScope
            .map((r) => r.userId)
            .filter((id): id is string => Boolean(id))
        ),
      ],
    ])
  );

  const nodes: GraphNode[] = [];
  for (const u of users) {
    if (u.deactivated) continue;
    const email = u.name ? u.email : undefined;
    const isAdmin = adminUserIds.has(u.id);
    nodes.push({
      id: u.id,
      kind: "user",
      name: userDisplayName(u),
      subtitle: isAdmin ? (email ? `Admin • ${email}` : "Admin") : email,
      isAdmin,
    });
  }
  for (const g of groups) {
    const count = groupMembers.get(g.id)?.length ?? 0;
    nodes.push({
      id: g.id,
      kind: "group",
      name: g.name,
      subtitle: `${count} member${count === 1 ? "" : "s"}`,
    });
  }
  for (const a of apps) nodes.push({ id: a.id, kind: "app", name: a.name });
  for (const sk of skills)
    nodes.push({ id: sk.id, kind: "skill", name: sk.name });
  for (const s of sharedSpaces)
    nodes.push({ id: s.id, kind: "space", name: s.name, spaceMode: s.mode });

  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const userIds = new Set(
    nodes.filter((n) => n.kind === "user").map((n) => n.id)
  );
  const groupIds = new Set(
    nodes.filter((n) => n.kind === "group").map((n) => n.id)
  );

  const edges: GraphEdge[] = [];
  const customRoleList = customRoles as { id: string; name: string }[];
  const labelOf = (g: ModelGrant) =>
    roleLabel(
      {
        builtinRoleId: g.builtinRoleId ?? undefined,
        customRoleId: g.customRoleId ?? undefined,
      },
      customRoleList
    );

  for (const g of groups) {
    for (const uid of groupMembers.get(g.id) ?? []) {
      if (!userIds.has(uid)) continue;
      edges.push({
        id: `mem:${uid}->${g.id}`,
        sourceId: uid,
        targetId: g.id,
        type: "membership",
        roleKey: "user_group_member",
        roleLabel: "Member",
      });
    }
  }

  const addScopeEdges = (scopeId: string, grants: readonly ModelGrant[]) => {
    for (const grant of grants) {
      const roleKey = roleKeyOf(grant);
      const label = labelOf(grant);
      if (grant.userId) {
        if (!userIds.has(grant.userId)) continue;
        edges.push({
          id: `dir:${grant.userId}->${scopeId}:${roleKey}`,
          sourceId: grant.userId,
          targetId: scopeId,
          type: "direct",
          roleKey,
          roleLabel: label,
        });
      } else if (grant.userGroupId) {
        if (!groupIds.has(grant.userGroupId)) continue;
        const viaGroupId = grant.userGroupId;
        edges.push({
          id: `dir:${viaGroupId}->${scopeId}:${roleKey}`,
          sourceId: viaGroupId,
          targetId: scopeId,
          type: "direct",
          roleKey,
          roleLabel: label,
        });
        const viaGroupName = nodeById.get(viaGroupId)?.name;
        for (const uid of groupMembers.get(viaGroupId) ?? []) {
          if (!userIds.has(uid)) continue;
          edges.push({
            id: `inh:${uid}->${scopeId}:${viaGroupId}:${roleKey}`,
            sourceId: uid,
            targetId: scopeId,
            type: "inherited",
            roleKey,
            roleLabel: label,
            viaGroupId,
            viaGroupName,
          });
        }
      }
    }
  };
  for (const a of apps) addScopeEdges(a.id, a.roleGrantsAsScope);
  for (const sk of skills) addScopeEdges(sk.id, sk.roleGrantsAsScope);
  for (const s of sharedSpaces) addScopeEdges(s.id, s.roleGrantsAsScope);

  for (const s of sharedSpaces) {
    if (s.roleGrantsAsScope.length === 0) continue;
    for (const a of apps) {
      if (
        isSupplierAvailableFromGrants(
          "space_uses_connected_apps",
          s.roleGrantsAsScope,
          a.roleGrantsAsScope,
          userGroupMemberships
        )
      ) {
        edges.push({
          id: `der:${a.id}->${s.id}`,
          sourceId: a.id,
          targetId: s.id,
          type: "derived",
          roleKey: "connected_app_user",
          roleLabel: "Available",
        });
      }
    }
    for (const sk of skills) {
      if (
        isSupplierAvailableFromGrants(
          "space_uses_skills",
          s.roleGrantsAsScope,
          sk.roleGrantsAsScope,
          userGroupMemberships
        )
      ) {
        edges.push({
          id: `der:${sk.id}->${s.id}`,
          sourceId: sk.id,
          targetId: s.id,
          type: "derived",
          roleKey: "skill_user",
          roleLabel: "Available",
        });
      }
    }
  }

  // Distinct people who can reach each connector/skill/space — directly or via
  // a group — shown as a member count like groups have.
  const scopeMembers = new Map<string, Set<string>>();
  const addMember = (scopeId: string, userId: string) => {
    let set = scopeMembers.get(scopeId);
    if (!set) {
      set = new Set();
      scopeMembers.set(scopeId, set);
    }
    set.add(userId);
  };
  const isResource = (kind: NodeKind | undefined) =>
    kind === "app" || kind === "skill" || kind === "space";
  for (const e of edges) {
    if (!isResource(nodeById.get(e.targetId)?.kind)) continue;
    if (e.type === "inherited") addMember(e.targetId, e.sourceId);
    else if (e.type === "direct" && nodeById.get(e.sourceId)?.kind === "user")
      addMember(e.targetId, e.sourceId);
  }
  for (const node of nodes) {
    if (!isResource(node.kind)) continue;
    const n = scopeMembers.get(node.id)?.size ?? 0;
    node.subtitle = `${n} member${n === 1 ? "" : "s"}`;
  }

  return { nodes, edges, nodeById };
}
